import { motion, useReducedMotion } from 'framer-motion'
import { ArrowUp, Sparkles } from 'lucide-react'
import { Button } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  heroHeadlineVariants,
  heroDescriptionVariants,
  heroButtonsVariants,
  reducedMotionVariants,
} from '../../lib/motion'
import { BEHIND_NAVBAR_CLASSES } from '../../lib/constants'
import type { HeroBaseProps } from './types'

export interface HeroCenterTableProps extends Omit<HeroBaseProps, 'variant'> {
  /** Column header labels */
  columns?: string[]
  /** Row data — each inner array maps to columns */
  rows?: string[][]
  /** Summary text for the floating pill at the bottom */
  summary?: string
}

/**
 * HeroCenterTable - Centered hero with a prop-driven data table below
 */
const HeroCenterTable = ({
  title = 'A Collection of Components Built With MDXUI',
  subtitle = 'Beautiful, accessible components built with React, Tailwind and MDXUI. Copy and paste into your projects with confidence.',
  callToAction = 'Get Started',
  callToActionUrl = '#',
  secondaryCallToAction = 'Learn More',
  secondaryCallToActionUrl = '#',
  columns = [],
  rows = [],
  summary,
  background,
  behindNavbar = true,
  className,
}: HeroCenterTableProps) => {
  const prefersReducedMotion = useReducedMotion()

  const headlineAnimation = prefersReducedMotion ? reducedMotionVariants : heroHeadlineVariants
  const descAnimation = prefersReducedMotion ? reducedMotionVariants : heroDescriptionVariants
  const buttonsAnimation = prefersReducedMotion ? reducedMotionVariants : heroButtonsVariants

  return (
    <div className={cn(
      'relative overflow-hidden',
      behindNavbar && BEHIND_NAVBAR_CLASSES,
      className
    )}>
      {background && (
        <div className="absolute inset-0">{background}</div>
      )}
      <motion.section
        className="relative z-10 py-32"
        variants={sectionVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="container max-w-4xl mx-auto">
          <div className="flex flex-col items-center text-center">
            <motion.h1
              variants={headlineAnimation}
              className="mb-6 max-w-4xl text-pretty text-4xl font-semibold lg:text-5xl"
            >
              {title}
            </motion.h1>
            <motion.div
              variants={descAnimation}
              className="text-muted-foreground mb-8 max-w-2xl"
            >
              <Markdown size="lg">{subtitle}</Markdown>
            </motion.div>

            <motion.div
              variants={buttonsAnimation}
              className="flex w-full flex-col items-center gap-3 sm:flex-row sm:justify-center"
            >
              {callToAction && callToActionUrl && (
                <Button asChild size="lg" className="w-full sm:w-auto">
                  <a href={callToActionUrl}>{callToAction}</a>
                </Button>
              )}
              {secondaryCallToAction && secondaryCallToActionUrl && (
                <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                  <a href={secondaryCallToActionUrl}>{secondaryCallToAction}</a>
                </Button>
              )}
            </motion.div>
          </div>

          {columns.length > 0 && (
            <motion.div
              variants={buttonsAnimation}
              className="relative mt-16"
            >
              <div className="bg-card/90 ring-border relative w-full overflow-hidden rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-transparent pb-12 shadow-2xl shadow-black/10 ring-1">
                {summary && (
                  <div className="max-w-xs absolute inset-x-0 bottom-4 z-10 mx-auto">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-emerald-400 via-teal-400 to-purple-400 opacity-15 blur" />
                    <div className="shadow-black/6.5 ring-border bg-card relative flex items-center gap-2 rounded-full p-1 shadow-md ring-1">
                      <div className="bg-card ring-border flex size-6 shrink-0 rounded-full ring-1 *:m-auto">
                        <Sparkles className="size-3" />
                      </div>
                      <div className="line-clamp-1 text-[13px]">
                        {summary}
                      </div>
                      <ArrowUp className="mr-2 size-3 shrink-0 opacity-65" />
                    </div>
                  </div>
                )}

                <div className="overflow-hidden rounded-t-[var(--radius-xl,calc(var(--radius)*1.5))]">
                <div className="mask-b-from-55% overflow-x-auto">
                  <table className="w-max min-w-full border-collapse">
                    <thead className="dark:bg-background bg-foreground/5">
                      <tr className="*:whitespace-nowrap *:border *:border-border *:px-3 *:py-1.5 *:text-left *:text-sm *:font-medium">
                        {columns.map((col, i) => (
                          <th key={i}>{col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="text-foreground/75 *:whitespace-nowrap *:border *:border-border *:p-2 *:px-3">
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex}>
                              {cellIndex === 0 ? (
                                <span className="text-foreground">{cell}</span>
                              ) : (
                                cell
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </motion.section>
    </div>
  )
}

export { HeroCenterTable }
